import React from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Phone, Check, Shield, Star, Crown, Gem } from 'lucide-react';
import { Button } from '../components/ui/button';
import { companyInfo } from '../data/mock';

const plans = [
  {
    name: 'Preferred',
    monthly: '$9.99',
    yearly: '$119.88',
    icon: Shield,
    color: 'from-[#622B62] to-[#7A3D7A]',
    badge: null,
    features: [
      'Remote monitoring',
      'Free diagnostics',
      'Maintenance visits',
      'Coil and blower cleaning',
      'Repair credit',
      'Service discounts',
      'Emergency priority',
      'Up to $300/yr trade-in credit',
    ],
  },
  {
    name: 'Prime',
    monthly: '$14.99',
    yearly: '$179.88',
    icon: Star,
    color: 'from-[#DA9C3A] to-[#E8B55A]',
    badge: 'Best Value',
    features: [
      'Everything in Preferred',
      'Enhanced priority',
      'Seasonal system checks',
      'Extended diagnostics',
      'Filter reminders',
      'Dedicated support',
      'Trade-in credit of up to $375/yr',
    ],
  },
  {
    name: 'Premier',
    monthly: '$24.99',
    yearly: '$299.88',
    icon: Crown,
    color: 'from-[#622B62] to-[#4A1F4A]',
    badge: null,
    features: [
      'Everything in Prime',
      '1 annual visit',
      'Basic repair coverage',
      '10% off additional services',
      'Thermostat optimization',
      'Air quality assessment',
      'Extended parts warranty',
      'Up to $399/yr trade-in credit',
    ],
  },
  {
    name: 'Prestige',
    monthly: '$39.99',
    yearly: '$479.88',
    icon: Gem,
    color: 'from-[#DA9C3A] to-[#C48A2A]',
    badge: 'Most Complete',
    features: [
      'Everything in Premier',
      '2 annual visits',
      'Advanced repair coverage',
      '15% off additional services',
      'Highest service priority',
      'Complete system protection',
      'VIP support',
      'Smart home integration assistance',
    ],
  },
];

const MaintenancePlansPage = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-[#FDFCF7]" data-testid="maintenance-plans-page">
      {/* Navigation Bar */}
      <div className="bg-white shadow-md sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
          <button
            onClick={() => navigate('/')}
            data-testid="back-to-home-btn"
            className="flex items-center gap-2 text-[#622B62] hover:text-[#DA9C3A] transition-colors font-medium"
          >
            <ArrowLeft size={20} />
            Back to Home
          </button>
          <a href={`tel:${companyInfo.phone}`}>
            <Button data-testid="call-btn" className="bg-[#DA9C3A] hover:bg-[#C48A2A] text-white font-semibold px-5 py-2 rounded-full">
              <Phone size={16} className="mr-2" />
              {companyInfo.phone}
            </Button>
          </a>
        </div>
      </div>

      {/* Hero Section */}
      <div className="bg-[#622B62] text-white py-16 relative overflow-hidden">
        <div className="absolute top-0 right-0 w-96 h-96 bg-[#7A3D7A] rounded-full -translate-y-1/2 translate-x-1/2 opacity-50" />
        <div className="absolute bottom-0 left-0 w-64 h-64 bg-[#4A1F4A] rounded-full translate-y-1/2 -translate-x-1/2 opacity-50" />

        <div className="max-w-4xl mx-auto px-4 text-center relative z-10">
          <div className="w-20 h-20 rounded-full bg-[#DA9C3A] flex items-center justify-center mx-auto mb-6">
            <Shield size={40} />
          </div>
          <h1 className="romano-heading text-4xl sm:text-5xl font-bold mb-4" data-testid="plans-heading">
            Maintenance Plans
          </h1>
          <p className="text-lg text-white/90 max-w-2xl mx-auto">
            Thank you for considering Romano Heating and Cooling. To help you compare your options, we've included a simple overview of our maintenance plans below.
          </p>
        </div>
      </div>

      {/* Plans Grid */}
      <div className="max-w-7xl mx-auto px-4 py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-8">
          {plans.map((plan) => {
            const Icon = plan.icon;
            return (
              <div
                key={plan.name}
                data-testid={`plan-card-${plan.name.toLowerCase()}`}
                className={`relative bg-white rounded-2xl shadow-lg border border-gray-100 overflow-hidden transition-transform hover:-translate-y-2 hover:shadow-xl ${
                  plan.badge ? 'ring-2 ring-[#DA9C3A]' : ''
                }`}
              >
                {plan.badge && (
                  <div className="absolute top-0 right-0 bg-[#DA9C3A] text-white text-xs font-bold px-4 py-1.5 rounded-bl-xl">
                    {plan.badge}
                  </div>
                )}

                {/* Plan Header */}
                <div className={`bg-gradient-to-br ${plan.color} text-white p-6 text-center`}>
                  <Icon size={36} className="mx-auto mb-3 opacity-90" />
                  <h2 className="romano-heading text-2xl font-bold">{plan.name}</h2>
                  <div className="mt-4">
                    <span className="text-3xl font-extrabold">{plan.monthly}</span>
                    <span className="text-white/80 text-sm">/mo</span>
                  </div>
                  <p className="text-white/70 text-sm mt-1">or {plan.yearly}/yr</p>
                </div>

                {/* Features */}
                <div className="p-6">
                  <ul className="space-y-3">
                    {plan.features.map((feature, i) => (
                      <li key={i} className="flex items-start gap-2.5">
                        <Check size={16} className="text-[#DA9C3A] mt-0.5 flex-shrink-0" />
                        <span className="text-gray-700 text-sm">{feature}</span>
                      </li>
                    ))}
                  </ul>

                  <Button
                      data-testid={`plan-cta-${plan.name.toLowerCase()}`}
                      className="w-full bg-[#622B62] hover:bg-[#4A1F4A] text-white font-semibold py-3 rounded-full mt-6"
                      onClick={() => navigate('/#contact')}
                    >
                      Get Started
                    </Button>
                </div>
              </div>
            );
          })}
        </div>

        {/* Commitment Note */}
        <div className="mt-16 bg-white rounded-2xl border border-gray-200 p-8 text-center max-w-3xl mx-auto">
          <p className="text-gray-600 leading-relaxed">
            All plans require a 12-month commitment. After the initial term, you may cancel with 30 days' notice. If you would like help selecting the best fit for your home and budget, I would be happy to assist.
          </p>
          <div className="mt-6 text-gray-700">
            <p className="font-medium">Best regards,</p>
            <p className="font-bold text-[#622B62] mt-2">Juliano Romano</p>
            <p className="text-sm">Romano Heating and Cooling</p>
            <p className="text-sm text-[#DA9C3A] font-semibold">916-274-9021</p>
            <p className="text-sm">RomanoHeatingandCooling.com</p>
          </div>
          <div className="mt-6 flex flex-col sm:flex-row gap-4 justify-center">
            <a href={`tel:${companyInfo.phone}`}>
              <Button data-testid="bottom-call-btn" className="bg-[#DA9C3A] hover:bg-[#C48A2A] text-white font-bold px-10 py-6 text-lg rounded-full shadow-xl">
                <Phone size={22} className="mr-2" />
                Call (916) 274-9021
              </Button>
            </a>
            <Button
              variant="outline"
              data-testid="bottom-contact-btn"
              className="border-2 border-[#622B62] text-[#622B62] hover:bg-[#622B62] hover:text-white font-bold px-10 py-6 text-lg rounded-full"
              onClick={() => navigate('/#contact')}
            >
              Contact Us Online
            </Button>
          </div>
        </div>
      </div>

      {/* Footer */}
      <div className="bg-[#622B62] text-white py-8">
        <div className="max-w-7xl mx-auto px-4 text-center">
          <div className="flex items-center justify-center gap-3 mb-4">
            <img
              src={companyInfo.logo}
              alt="Romano Heating & Cooling"
              className="h-24 w-24 object-contain rounded-lg"
            />
            <div className="text-left">
              <h3 className="font-bold romano-heading">Romano Heating & Cooling</h3>
              <p className="text-white/70 text-sm">1316 Church Street, Roseville, CA 95678</p>
            </div>
          </div>
          <p className="text-[#DA9C3A] font-semibold mb-2">(916) 274-9021</p>
          <p className="text-white/60 text-sm">
            RomanoHeatingandCooling.com
          </p>
          <p className="text-white/40 text-sm mt-1">
            &copy; {new Date().getFullYear()} Romano Heating and Cooling. All Rights Reserved.
          </p>
        </div>
      </div>
    </div>
  );
};

export default MaintenancePlansPage;
